import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { SignOverview } from '../../model/sign/sign-overview';
import { SignMinimal } from '../../model/sign/sign-minimal';
import { LoadingService } from '../loading.service';
import { SnackService } from '../snack.service';
import { ProperPageResult } from '../../model/proper-page-result';
import { SignDetail } from '../../model/sign/sign-detail';
import { SignPositionTypesChoices } from '../../model/sign/enums/sign-position-type';
import { Observable } from 'rxjs';
import { _delete, _get, _post, _put, DeserializeVoid } from '../base.service';
import { SignStockOfferSentStatusRequest } from '../../model/sign-stock/sign-stock-offer-sent-status-request';
import { SignStockSetRecupMethodRequest } from '../../model/sign-stock/sign-stock-set-recup-method-request';

type SignRoofLocationSaveRequest = {
  id: number;
  roofLocation: string;
};

@Injectable({
  providedIn: 'root',
})
export class SignsControllerRequestService {
  private static BASE_URL = '/signs';
  private readonly get: <T>(
    path: string,
    deserialize: (response: any) => T
  ) => Observable<T>;
  private readonly post: <T>(
    path: string,
    body: SignDetail,
    deserialize: (response: any) => T
  ) => Observable<T>;
  private readonly put: <T>(
    path: string,
    body:
      | SignDetail
      | SignRoofLocationSaveRequest
      | SignStockOfferSentStatusRequest
      | SignStockSetRecupMethodRequest,
    deserialize: (response: any) => T
  ) => Observable<T>;

  private readonly delete: (path: string) => Observable<void>;
  private readonly deserialize = (res) => res as SignDetail;

  private readonly deserializePage = (res) => {
    const result: ProperPageResult<SignOverview> = new ProperPageResult();
    result.totalItems = res.totalItems;
    result.items = res.items;
    result.items.forEach((o) => {
      if (o.signMeasurements) {
        o.signMeasurementsDisplay =
          o.signMeasurements.width + ' x ' + o.signMeasurements.height;
      }
      if (o.position) {
        if (o.position.value === SignPositionTypesChoices.LEFT.value) {
          o.positionDisplay = 'L';
        } else if (o.position.value === SignPositionTypesChoices.RIGHT.value) {
          o.positionDisplay = 'R';
        } else {
          o.positionDisplay = '-';
        }
      }
      if (o.participatingCompanies && o.participatingCompanies.length > 0) {
        o.companiesDisplay = o.participatingCompanies[0].company.regularName;
        for (let i = 1; i < o.participatingCompanies.length; i++) {
          o.companiesDisplay += `, ${o.participatingCompanies[i].company.regularName}`;
        }
      }
    });
    return result;
  };

  private readonly deserializeMinimalPage = (res) => {
    const result: ProperPageResult<SignMinimal> = new ProperPageResult();
    result.totalItems = res.totalItems;
    result.items = res.items;
    result.items.forEach((o) => {
      if (o.signMeasurements) {
        o.signMeasurementsDisplay =
          o.signMeasurements.width + ' x ' + o.signMeasurements.height;
      }
    });
    return result;
  };

  constructor(
    http: HttpClient,
    snackService: SnackService,
    loadingService: LoadingService,
    router: Router
  ) {
    const requestServices = {
      http,
      loadingService,
      router,
      snackService,
    };
    this.get = _get(SignsControllerRequestService.BASE_URL, requestServices);
    this.post = _post(SignsControllerRequestService.BASE_URL, requestServices);
    this.put = _put(SignsControllerRequestService.BASE_URL, requestServices);
    this.delete = _delete(
      SignsControllerRequestService.BASE_URL,
      requestServices
    );
  }

  public create(sign: SignDetail): Promise<SignDetail> {
    return this.post('', sign, this.deserialize).toPromise();
  }

  public update(sign: SignDetail): Promise<SignDetail> {
    return this.put('', sign, this.deserialize).toPromise();
  }

  public updateRoofLocation(id: number, roofLocation: string): Promise<void> {
    const request: SignRoofLocationSaveRequest = { id, roofLocation };
    return this.put(
      '/updateRoofLocation',
      request,
      DeserializeVoid
    ).toPromise();
  }

  public archive(signId: number): Promise<void> {
    return this.delete(`/archive/${signId}`).toPromise();
  }

  public getPaged(
    filters: string,
    sort: string,
    sortAscending: boolean,
    pageSize: number,
    page: number,
    showArchived: boolean
  ): Promise<ProperPageResult<SignOverview>> {
    const filtersParam: string = `filters=${filters}`;
    const pageableParam: string = `page=${page}&size=${pageSize}&sortDirection=${
      sortAscending ? 'ASC' : 'DESC'
    }&sortProperty=${sort}`;
    const showArchivedParam: string = `showArchived=${showArchived}`;
    //This is gonna be common, should extract/generalize this later
    const pathParams: string = `?${filtersParam}&${pageableParam}&${showArchivedParam}`;

    return this.get(pathParams, this.deserializePage).toPromise();
  }

  public getPagedMinimal(
    companyIds: number[],
    filters: string,
    sort: string,
    sortAscending: boolean,
    pageSize: number,
    page: number,
    showArchived: boolean
  ): Promise<ProperPageResult<SignMinimal>> {
    let companyIdsString: string = '';
    if (companyIds.length > 0) {
      companyIdsString += companyIds[0].toString();
    }
    for (let index = 1; index < companyIds.length; index++) {
      const id = companyIds[index];

      companyIdsString += `|${id}`;
    }

    const companyIdsParam: string = `companyIds=${companyIdsString}`;
    const filtersParam: string = `filters=${filters}`;
    const pageableParam: string = `page=${page}&size=${pageSize}&sortDirection=${
      sortAscending ? 'ASC' : 'DESC'
    }&sortProperty=${sort}`;
    const showArchivedParam: string = `showArchived=${showArchived}`;

    const pathParams: string = `/minimal?${companyIdsParam}&${filtersParam}&${pageableParam}&${showArchivedParam}`;

    return this.get(pathParams, this.deserializeMinimalPage).toPromise();
  }

  public getById(id: number): Promise<SignDetail> {
    return this.get(`/${id}`, this.deserialize).toPromise();
  }

  public setOfferSentStatus(
    request: SignStockOfferSentStatusRequest
  ): Promise<void> {
    return this.put('/stock-offer-sent', request, DeserializeVoid).toPromise();
  }

  public setRecupMethodStatus(
    request: SignStockSetRecupMethodRequest
  ): Promise<void> {
    return this.put('/recup-method', request, DeserializeVoid).toPromise();
  }
}
